import { useState, useRef, ChangeEvent, FormEvent, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, serverTimestamp, arrayUnion, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, MapPin, Droplet } from 'lucide-react';
import ImageUpload from './ImageUpload';
import Modal from './Modal';
import { PondingLog, PondingLogFormData, ImageData } from './types';

interface PondingLogFormProps {
    eventId: string;
    editingLog?: PondingLog | null;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export default function PondingLogForm({ eventId, editingLog, onSuccess, onCancel }: PondingLogFormProps) {
    const [formData, setFormData] = useState<PondingLogFormData>({
        reporter: '',
        latitude: '',
        longitude: '',
        region: '',
        city: '',
        country: '',
        date: '',
        depthMm: '',
        source: '',
        link: '',
    });

    const [streetImages, setStreetImages] = useState<File[]>([]);
    const [eventImages, setEventImages] = useState<File[]>([]);
    const streetUploadRef = useRef<{ clear: () => void }>(null);
    const eventUploadRef = useRef<{ clear: () => void }>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error' | ''; text: string }>({
        type: '',
        text: ''
    });
    const [showStreetModal, setShowStreetModal] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);

    useEffect(() => {
        if (editingLog) {
            setFormData({
                reporter: editingLog.reporter || '',
                latitude: editingLog.latitude?.toString() || '',
                longitude: editingLog.longitude?.toString() || '',
                region: editingLog.region || '',
                city: editingLog.city || '',
                country: editingLog.country || '',
                date: editingLog.date instanceof Timestamp
                    ? editingLog.date.toDate().toISOString().split('T')[0]
                    : editingLog.date instanceof Date
                        ? editingLog.date.toISOString().split('T')[0]
                        : '',
                depthMm: editingLog.depthMm?.toString() || '',
                source: editingLog.source || '',
                link: editingLog.link || '',
            });
        } else {
            setFormData({
                reporter: '',
                latitude: '',
                longitude: '',
                region: '',
                city: '',
                country: '',
                date: '',
                depthMm: '',
                source: '',
                link: '',
            });
        }
    }, [editingLog]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setFormData({
            reporter: '',
            latitude: '',
            longitude: '',
            region: '',
            city: '',
            country: '',
            date: '',
            depthMm: '',
            source: '',
            link: '',
        });
        setStreetImages([]);
        setEventImages([]);
        setMessage({ type: '', text: '' });
        if (streetUploadRef.current?.clear) streetUploadRef.current.clear();
        if (eventUploadRef.current?.clear) eventUploadRef.current.clear();
    };

    const uploadMultipleImages = async (files: File[], folder: string): Promise<ImageData[]> => {
        const results: ImageData[] = [];
        for (const file of files) {
            const timestamp = Date.now();
            const path = `${folder}/${timestamp}-${file.name}`;
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            results.push({ url, path });
        }
        return results;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const docData: Record<string, unknown> = {
                eventId,
                reporter: formData.reporter,
                region: formData.region,
                city: formData.city,
                country: formData.country,
                source: formData.source,
                link: formData.link,
            };

            // Parse numeric fields
            if (formData.latitude) {
                const lat = parseFloat(formData.latitude);
                if (!Number.isNaN(lat)) docData.latitude = lat;
            }

            if (formData.longitude) {
                const lon = parseFloat(formData.longitude);
                if (!Number.isNaN(lon)) docData.longitude = lon;
            }

            if (formData.depthMm) {
                const depth = parseFloat(formData.depthMm);
                if (!Number.isNaN(depth)) docData.depthMm = depth;
            }

            if (formData.date) {
                const d = new Date(formData.date);
                if (!Number.isNaN(d.getTime())) docData.date = d;
            }

            // Upload images
            if (streetImages.length > 0) {
                const streetResults = await uploadMultipleImages(streetImages, 'street');
                docData.streetViewImages = streetResults;
            } else if (editingLog && editingLog.streetViewImages) {
                docData.streetViewImages = editingLog.streetViewImages;
            }

            if (eventImages.length > 0) {
                const eventResults = await uploadMultipleImages(eventImages, 'event');
                docData.eventViewImages = eventResults;
            } else if (editingLog && editingLog.eventViewImages) {
                docData.eventViewImages = editingLog.eventViewImages;
            }

            if (editingLog) {
                // Update existing ponding log
                docData.updatedAt = serverTimestamp();
                await updateDoc(doc(db, 'ponding-logs', editingLog.id), docData);
                setMessage({
                    type: 'success',
                    text: 'Ponding log updated successfully!'
                });
            } else {
                // Create new ponding log
                docData.createdAt = serverTimestamp();
                const pondingRef = await addDoc(collection(db, 'ponding-logs'), docData);

                // Update event's pondings array
                await updateDoc(doc(db, 'events', eventId), {
                    pondings: arrayUnion(pondingRef.id)
                });

                setMessage({
                    type: 'success',
                    text: 'Ponding log added successfully!'
                });
            }

            resetForm();
            if (onSuccess) onSuccess();

        } catch (error) {
            console.error('Error submitting ponding log:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setMessage({
                type: 'error',
                text: `Error: ${errorMessage}`
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 pb-2">
            {message.text && (
                <Card className={`border-2 ${message.type === 'success'
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-500 bg-red-50'
                    }`}>
                    <CardContent className="p-3 flex items-center gap-2">
                        <AlertCircle className={`h-4 w-4 ${message.type === 'success' ? 'text-green-600' : 'text-red-600'
                            } flex-shrink-0`} />
                        <span className={`text-xs ${message.type === 'success' ? 'text-green-700' : 'text-red-700'
                            }`}>
                            {message.text}
                        </span>
                    </CardContent>
                </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            Location Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="latitude" className="text-xs font-medium">
                                    Latitude *
                                </Label>
                                <Input
                                    id="latitude"
                                    name="latitude"
                                    type="number"
                                    step="any"
                                    value={formData.latitude}
                                    onChange={handleInputChange}
                                    placeholder="13.0827"
                                    className="text-sm"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="longitude" className="text-xs font-medium">
                                    Longitude *
                                </Label>
                                <Input
                                    id="longitude"
                                    name="longitude"
                                    type="number"
                                    step="any"
                                    value={formData.longitude}
                                    onChange={handleInputChange}
                                    placeholder="80.2707"
                                    className="text-sm"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="city" className="text-xs font-medium">
                                City *
                            </Label>
                            <Input
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                placeholder="Chennai"
                                className="text-sm"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="region" className="text-xs font-medium">
                                Region *
                            </Label>
                            <Input
                                id="region"
                                name="region"
                                value={formData.region}
                                onChange={handleInputChange}
                                placeholder="Tamil Nadu"
                                className="text-sm"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="country" className="text-xs font-medium">
                                Country *
                            </Label>
                            <Input
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                placeholder="India"
                                className="text-sm"
                                required
                            />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Droplet className="h-4 w-4" />
                            Measurement Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="space-y-2">
                            <Label htmlFor="date" className="text-xs font-medium">
                                Date *
                            </Label>
                            <Input
                                id="date"
                                name="date"
                                type="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                className="text-sm"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="depthMm" className="text-xs font-medium">
                                Depth (mm) *
                            </Label>
                            <Input
                                id="depthMm"
                                name="depthMm"
                                type="number"
                                step="0.1"
                                value={formData.depthMm}
                                onChange={handleInputChange}
                                placeholder="150"
                                className="text-sm"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="reporter" className="text-xs font-medium">
                                Reporter *
                            </Label>
                            <Input
                                id="reporter"
                                name="reporter"
                                value={formData.reporter}
                                onChange={handleInputChange}
                                placeholder="Name of person reporting"
                                className="text-sm"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="source" className="text-xs font-medium">
                                Source
                            </Label>
                            <Input
                                id="source"
                                name="source"
                                value={formData.source}
                                onChange={handleInputChange}
                                placeholder="e.g., Field observation"
                                className="text-sm"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="link" className="text-xs font-medium">
                                Reference Link
                            </Label>
                            <Input
                                id="link"
                                name="link"
                                type="url"
                                value={formData.link}
                                onChange={handleInputChange}
                                placeholder="https://..."
                                className="text-sm"
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Images</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            {editingLog?.streetViewImages && editingLog.streetViewImages.length > 0 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowStreetModal(true)}
                                    className="h-6 text-xs mb-2 w-full"
                                >
                                    📸 view ({editingLog.streetViewImages.length})
                                </Button>
                            )}
                            <ImageUpload
                                ref={streetUploadRef}
                                label="Street View"
                                onFilesChange={setStreetImages}
                            />
                        </div>

                        <div>
                            {editingLog?.eventViewImages && editingLog.eventViewImages.length > 0 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowEventModal(true)}
                                    className="h-6 text-xs mb-2 w-full"
                                >
                                    📸 view ({editingLog.eventViewImages.length})
                                </Button>
                            )}
                            <ImageUpload
                                ref={eventUploadRef}
                                label="Event View"
                                onFilesChange={setEventImages}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="flex gap-2 justify-end pt-2">
                {onCancel && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={loading}
                        size="sm"
                        className="h-8 text-xs"
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    type="submit"
                    disabled={loading}
                    size="sm"
                    className="h-8 text-xs"
                >
                    {loading ? 'Saving...' : editingLog ? 'Update Ponding Log' : 'Add Ponding Log'}
                </Button>
            </div>

            {/* Street View Images Modal */}
            {showStreetModal && editingLog?.streetViewImages && (
                <Modal
                    isOpen={showStreetModal}
                    onClose={() => setShowStreetModal(false)}
                    title="Street View Images"
                >
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        {editingLog.streetViewImages.map((img, i) => (
                            <img
                                key={i}
                                src={img.url}
                                alt={`Street ${i + 1}`}
                                className="w-full h-32 object-cover rounded border"
                            />
                        ))}
                    </div>
                </Modal>
            )}

            {/* Event View Images Modal */}
            {showEventModal && editingLog?.eventViewImages && (
                <Modal
                    isOpen={showEventModal}
                    onClose={() => setShowEventModal(false)}
                    title="Event View Images"
                >
                    <div className="grid grid-cols-3 gap-2 mt-2">
                        {editingLog.eventViewImages.map((img, i) => (
                            <img
                                key={i}
                                src={img.url}
                                alt={`Event ${i + 1}`}
                                className="w-full h-32 object-cover rounded border"
                            />
                        ))}
                    </div>
                </Modal>
            )}
        </form>
    );
}
